import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SUPPORT_TEAMS } from "@/lib/support";
import { Reveal, Eyebrow } from "@/components/site/Motion";
import { QuoteButton } from "@/components/site/Nav";

export const Welcome = () => (
  <section id="welcome" className="bg-white px-4 py-24 sm:px-6 lg:px-12 lg:py-32" data-testid="welcome-section">
    <div className="mx-auto max-w-5xl text-center">
      <Reveal>
        <Eyebrow>Welcome to 866Zerofees.com</Eyebrow>
        <h2 className="mt-6 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-[#050B1F] sm:text-5xl lg:text-7xl">
          Fees out of the way. <span className="text-emerald-600">People in your corner.</span>
        </h2>
        <p className="mx-auto mt-8 max-w-2xl text-base text-slate-700 md:text-lg">
          We combine modern processing with a team that knows your account by name, so every sale moves your business forward instead of eating into it.
        </p>
      </Reveal>
    </div>
  </section>
);

const WAYS = [
  { t: "In-Person", d: "Countertop terminals and smart POS for the counter.", img: "/img/tap.jpg" },
  { t: "Online", d: "Accept payments on your website and by invoice.", img: "/img/phone.jpg" },
  { t: "Omnichannel", d: "One account for every way your customers pay.", img: "/img/pos.jpg" },
];

export const Merchants = () => (
  <section id="merchants" className="relative overflow-hidden bg-[#050B1F] px-4 py-24 text-white sm:px-6 lg:px-12 lg:py-32 grain" data-testid="merchants-section">
    <div className="grid-bg absolute inset-0" />
    <div className="relative mx-auto max-w-7xl">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <Reveal>
          <Eyebrow light>For merchants</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">Take payments <span className="text-lime-400">every way.</span></h2>
          <p className="mt-4 max-w-md text-blue-100/85">Make every sale smooth, secure and stress-free.</p>
        </Reveal>
        <Reveal delay={0.1}>
          <Link to="/solutions" data-testid="merchants-solutions-link" className="group inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold hover:border-lime-300 hover:text-lime-300">
            Explore solutions <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {WAYS.map((it, i) => (
          <Reveal key={it.t} delay={i * 0.1}>
            <Link to="/solutions" data-testid={`merchant-way-${i}`}>
              <motion.div whileHover="hover" initial="rest" className="group relative overflow-hidden rounded-[2rem]">
                <motion.img variants={{ rest: { scale: 1 }, hover: { scale: 1.08 } }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} src={it.img} alt={it.t} className="aspect-[4/5] w-full object-cover" />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_35%,rgba(5,11,31,0.92)_100%)]" />
                <div className="absolute inset-x-6 bottom-6">
                  <div className="flex items-center justify-between">
                    <p className="font-display text-3xl font-extrabold tracking-tight">{it.t}</p>
                    <span className="grid h-10 w-10 place-items-center rounded-full bg-lime-400 text-[#050B1F] transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="h-5 w-5" /></span>
                  </div>
                  <p className="mt-2 text-sm text-blue-100/90">{it.d}</p>
                </div>
              </motion.div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Support = () => (
  <section id="support-teams" className="bg-[#EAF3FF] px-4 py-24 sm:px-6 lg:px-12 lg:py-32" data-testid="support-section">
    <div className="mx-auto max-w-7xl">
      <Reveal className="max-w-2xl">
        <Eyebrow>Support</Eyebrow>
        <h2 className="mt-4 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-[#050B1F] sm:text-5xl lg:text-6xl">Support as serious as <span className="text-emerald-600">you are.</span></h2>
        <p className="mt-5 text-slate-700">Real teams for every part of your payments, not a ticket queue.</p>
      </Reveal>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {SUPPORT_TEAMS.map(({ slug, icon: I, title: t, short: d }, i) => (
          <Reveal key={slug} delay={i * 0.08}>
            <Link to={`/support/${slug}`} data-testid={`support-card-${i}`} className="block h-full">
            <motion.div whileHover={{ y: -8 }} transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="group flex h-full flex-col rounded-3xl border border-[#0B2A6F]/10 bg-white p-7 transition-shadow duration-500 hover:shadow-[0_30px_70px_-30px_rgba(11,42,111,0.45)]">
              <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[#0B2A6F] text-lime-300 transition-colors duration-500 group-hover:bg-lime-400 group-hover:text-[#050B1F]"><I className="h-6 w-6" /></span>
              <p className="mt-8 font-display text-2xl font-extrabold tracking-tight text-[#050B1F]">{t}</p>
              <p className="mt-3 flex-1 text-sm text-slate-700">{d}</p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#0B2A6F]">Learn more <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" /></span>
            </motion.div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Human = () => (
  <section className="bg-white px-4 py-24 sm:px-6 lg:px-12 lg:py-32" data-testid="human-section">
    <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-2">
      <Reveal>
        <div className="relative">
          <img src="/img/owner.jpg" alt="Small business owner at her shop" className="aspect-[5/4] w-full rounded-[2rem] object-cover" />
          <div className="absolute -bottom-6 left-6 rounded-2xl bg-[#050B1F] px-5 py-4 text-white shadow-2xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-lime-300">Your account manager</p>
            <p className="font-display text-lg font-bold">One call away.</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <Eyebrow>The 866Zerofees difference</Eyebrow>
        <h2 className="mt-4 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-[#050B1F] sm:text-5xl lg:text-6xl">The human side of <span className="text-emerald-600">payments.</span></h2>
        <p className="mt-6 max-w-lg text-slate-700">Payments are often impersonal and confusing. We bring the people back: transparent pricing explained in plain English, and a team that picks up the phone.</p>
        <Link to="/about" data-testid="human-about-link" className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#0B2A6F] px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#050B1F]">
          Meet us <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export const Spotlight = () => (
  <section className="px-4 pb-24 sm:px-6 lg:px-12 lg:pb-32" data-testid="spotlight-section">
    <Reveal className="mx-auto max-w-7xl">
      <div className="relative grid overflow-hidden rounded-[2.5rem] bg-[#0B2A6F] text-white lg:grid-cols-2">
        <div className="relative z-10 p-10 sm:p-14 lg:p-16">
          <Eyebrow light>Spotlight</Eyebrow>
          <h2 className="mt-4 font-display text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl">Free equipment, <span className="text-lime-300">ready to go.</span></h2>
          <p className="mt-5 max-w-md text-blue-100/90">Qualifying merchants get modern terminals at no cost, set up by our tech support team before day one.</p>
          <QuoteButton testid="spotlight-quote-btn" className="mt-8">See if you qualify</QuoteButton>
        </div>
        <div className="relative min-h-[320px]">
          <img src="/img/terminal-blue.jpg" alt="Modern card terminal" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,#0B2A6F_0%,transparent_40%)] max-lg:bg-[linear-gradient(180deg,#0B2A6F_0%,transparent_40%)]" />
        </div>
      </div>
    </Reveal>
  </section>
);
